/**
 * TerminalWidget — interactive xterm.js terminal bound to the canvas's e2b sandbox.
 *
 * Fill-mode widget (preset `scaleContent: false`): renders `w-full h-full` and uses
 * a FitAddon + ResizeObserver so resizing the node re-fits rows/cols live, like a
 * native terminal window. On connect it provisions the sandbox via zensei-api (auth + can_edit +
 * a short-lived token) and then opens its WebSocket DIRECTLY to the sandbox's public
 * e2b URL — PTY bytes never transit zensei-api or neuraflow. Each widget keeps a stable
 * `terminalSessionId` so it maps to its own persistent `tmux` session on the container.
 *
 * View-only (`canEdit === false`, including the public canvas-view SPA) never provisions or
 * connects — sandbox mint requires auth + can_edit, and retrying just hammers orgAPI while
 * leaving viewers stuck on connecting/error overlays.
 */
import { orgAPI } from "@/api/api";
import type { WidgetNodeProps } from "@/pages/canvas/types/canvasNodeData";
import { cn } from "@/utils";
import { FitAddon } from "@xterm/addon-fit";
import { WebLinksAddon } from "@xterm/addon-web-links";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";
import { isAxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useCanvasApi } from "../../../context/CanvasContext";
import { useCanvasRoute } from "../../../context/CanvasRouteContext";
import { useCanvasUi } from "../../../context/CanvasUiContext";
import {
  getCanvasSandboxProvision,
  invalidateCanvasSandboxProvision,
  releaseCanvasSandboxProvision,
  retainCanvasSandboxProvision,
} from "./sandboxProvision";
import { DEFAULT_TERMINAL_THEME_ID, getTerminalTheme } from "./themes";

const MAX_RECONNECT_ATTEMPTS = 6;
// Larger budget for transient provision failures (5xx / network): with the 10s backoff cap this is
// ~3 min of patient retrying, enough to ride out a zensei-api deploy / ALB blip before giving up with
// an error. Bounded so a genuine outage or expired session doesn't spin "Reconnecting…" forever.
const MAX_PROVISION_RETRY_ATTEMPTS = 20;

type ConnState =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "error"
  | "unavailable";

const TERMINAL_FONT =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Code", "Roboto Mono", "Courier New", monospace';

export function TerminalWidget({ id, data }: WidgetNodeProps) {
  const canvas = useCanvasApi();
  const { canEdit } = canvas;
  const { canvasId } = useCanvasRoute();
  const { isCapture } = useCanvasUi();

  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const unmountedRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  // Counts reconnects triggered by an actual WebSocket close (not pre-socket connect/provision
  // failures, which only feed reconnectAttemptsRef for backoff). This gates shared-cache
  // invalidation: a failed connect before any socket must not make the first real close look like a
  // cached-URL retry and re-provision the whole canvas. Reset on mount and on a delivered frame.
  const socketReconnectAttemptsRef = useRef(0);
  // Separate budget for transient provision-endpoint failures (5xx / network, e.g. zensei-api
  // deploying or the ALB returning 502/503/504). These keep retrying patiently instead of burning the
  // bounded WS-reconnect budget, so a deploy that outlasts ~40s doesn't strand the user behind "error".
  const provisionRetryRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True when the last socket closed with 1008 (unauthorized) — used for a clearer give-up message.
  const authRejectedRef = useRef(false);

  // The session id maps this widget to its own tmux session. Node data
  // (`terminalSessionId`) is the SINGLE source of truth — the toolbar's agent
  // control grant and the activity pulse both key off it — so the socket must
  // use the same value, not a frozen local copy. `mintedSessionIdRef` is only a
  // stable fallback for a brand-new widget whose id hasn't been persisted/synced
  // yet; once data carries an id (e.g. synced from another collaborator) we adopt
  // it and reconnect (see the effect dep below).
  const mintedSessionIdRef = useRef<string>(uuidv4());
  const sessionId =
    (typeof data.terminalSessionId === "string" && data.terminalSessionId) ||
    mintedSessionIdRef.current;

  const [status, setStatus] = useState<ConnState>(() =>
    canEdit && !isCapture ? "connecting" : "unavailable",
  );
  const [errorMessage, setErrorMessage] = useState<string>("");

  const themeId = String(data.theme ?? DEFAULT_TERMINAL_THEME_ID);

  // ── Create the terminal + connect once on mount ───────────────────────────
  useEffect(() => {
    // Public share / view-only: never call orgAPI.sandboxProvision or open a socket.
    // canEdit previously only gated persisting terminalSessionId, so published canvases
    // with terminals hammered unauthenticated provision/reconnect loops.
    //
    // Captures bail for a different reason: the capture token is the owner's, so
    // canEdit is true and the terminal would connect and bake live session output
    // into the hero image and the public OG card.
    if (!canEdit || isCapture) {
      setStatus("unavailable");
      setErrorMessage("");
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    // Reset on (re)mount: StrictMode runs mount→cleanup→mount in dev, and the
    // cleanup sets this true. Without resetting, the second (real) mount would
    // see `true` and connect() would bail before opening the socket.
    unmountedRef.current = false;
    // Fresh connection lifecycle: these refs persist across effect re-runs (sessionId / canvasId
    // change), so reset both retry budgets. Otherwise a leftover reconnectAttemptsRef > 0 makes the
    // first onclose after a session-sync re-run bust the shared canvas cache (the >0 branch in
    // onclose), and a leftover provisionRetryRef could prematurely exhaust the patient-retry budget.
    reconnectAttemptsRef.current = 0;
    socketReconnectAttemptsRef.current = 0;
    provisionRetryRef.current = 0;
    // Per-run cancellation: `unmountedRef` is shared and gets reset to false by
    // the NEXT effect run, so a connect() awaiting a provision across a teardown
    // would wrongly see false and open a stale socket. This flag is set once in
    // this run's cleanup and never reset, so such an in-flight connect bails.
    let cancelled = false;
    // Identity (org/user) this widget retained the canvas provision under — captured once we have it
    // (it's fetched async in connect()), so cleanup can release the exact same refcount key.
    let provisionIdentity: {
      orgId: string;
      userId: string;
      canvasId: string;
    } | null = null;
    console.log("[Terminal] mount id=%s canvasId=%s", id, canvasId);

    const term = new Terminal({
      fontFamily: TERMINAL_FONT,
      fontSize: 13,
      cursorBlink: true,
      allowProposedApi: true,
      scrollback: 5000,
      theme: getTerminalTheme(themeId).theme,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.loadAddon(new WebLinksAddon());
    term.open(container);
    termRef.current = term;
    fitRef.current = fit;

    const safeFit = () => {
      try {
        fit.fit();
      } catch {
        /* element not laid out yet */
      }
    };

    const sendResize = () => {
      const socket = socketRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      socket.send(
        JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows }),
      );
    };

    const scheduleReconnect = () => {
      if (unmountedRef.current) return;
      if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        setStatus("error");
        setErrorMessage(
          authRejectedRef.current
            ? "Terminal authorization failed. Reopen the widget to retry."
            : "Connection lost. Reopen the widget to retry.",
        );
        return;
      }
      const attempt = reconnectAttemptsRef.current++;
      const delay = Math.min(1500 * 2 ** attempt, 10000);
      console.log(
        "[Terminal] scheduleReconnect attempt=%d delay=%d",
        attempt,
        delay,
      );
      setStatus("reconnecting");
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    // Patient retry for a transient provision failure (5xx / network). A 503 means "the service is
    // briefly down, try again", so we keep gently retrying (capped backoff) in the "reconnecting" state
    // on its own larger budget — long enough to ride out a deploy, but still bounded so a genuine
    // outage / expired session ends in an error rather than spinning forever. Deduped per canvas +
    // auto-cancelled on unmount, so this is at most one request per ~10s per canvas.
    const scheduleProvisionRetry = () => {
      if (unmountedRef.current) return;
      if (provisionRetryRef.current >= MAX_PROVISION_RETRY_ATTEMPTS) {
        setStatus("error");
        setErrorMessage(
          "The terminal service is temporarily unavailable. Reopen the widget to retry.",
        );
        return;
      }
      const attempt = provisionRetryRef.current++;
      const delay = Math.min(1500 * 2 ** attempt, 10000);
      console.log(
        "[Terminal] scheduleProvisionRetry attempt=%d delay=%d",
        attempt,
        delay,
      );
      setStatus("reconnecting");
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    async function connect() {
      if (unmountedRef.current || cancelled || !canvasId) return;
      try {
        const [orgId, userId] = await Promise.all([
          orgAPI.getOrgID(),
          orgAPI.getUserID(),
        ]);
        if (unmountedRef.current || cancelled || !orgId || !userId) return;

        // Retain the shared provision once (connect() re-runs on reconnect); cleanup releases it so
        // the cached token is dropped when the last widget on this canvas unmounts.
        if (!provisionIdentity) {
          provisionIdentity = { orgId, userId, canvasId };
          retainCanvasSandboxProvision(orgId, userId, canvasId);
        }

        // Provision via zensei-api (auth0 + can_edit + token mint), then connect the xterm.js socket
        // DIRECTLY to the sandbox's public e2b URL — PTY bytes never transit zensei-api or neuraflow.
        // Shared + deduped per canvas: {terminal_server_url, terminal_server_token} are
        // canvas-scoped, so every widget rides one provision (one sandbox + one token mint). onclose
        // re-provisions only when the cache is actually suspect (1008 or a failed cached-URL
        // reconnect) — see below.
        const { terminal_server_url, terminal_server_token } =
          await getCanvasSandboxProvision(orgId, userId, canvasId);
        if (unmountedRef.current || cancelled) return;
        // Provision succeeded — reset the patient-retry backoff so a later transient failure starts fresh.
        provisionRetryRef.current = 0;

        const url =
          `${terminal_server_url}/?token=${encodeURIComponent(terminal_server_token)}` +
          `&session_id=${encodeURIComponent(sessionId)}` +
          `&cols=${term.cols}&rows=${term.rows}`;

        console.log(
          "[Terminal] connecting",
          url.replace(/token=[^&]+/, "token=…"),
        );
        // Defensively close any socket still referenced from a prior connect()
        // and detach its handlers, so an overlapping retry can't leave two live
        // connections or let a stale onclose schedule another reconnect. In the
        // normal reconnect path this is already null (cleared in onclose).
        if (socketRef.current) {
          socketRef.current.onclose = null;
          try {
            socketRef.current.close();
          } catch {
            /* already closing */
          }
        }
        const socket = new WebSocket(url);
        socket.binaryType = "arraybuffer";
        socketRef.current = socket;
        let gotData = false;
        // Set when the server sends a {type:"status", state:"error"} frame — a
        // fatal, server-reported condition we must NOT reconnect through.
        let fatalError = false;

        socket.onopen = () => {
          console.log("[Terminal] OPEN");
          // Do NOT reset the reconnect backoff here: an unauthorized close (1008) arrives right
          // AFTER the upgrade opens, so resetting on open would defeat the backoff and hammer the
          // server forever. We only count the connection as healthy once it delivers a frame.
          setStatus("connected");
          sendResize();
        };
        socket.onmessage = (ev) => {
          if (typeof ev.data === "string") {
            try {
              const msg = JSON.parse(ev.data);
              if (msg?.type === "status" && msg.state === "error") {
                // Server-reported fatal error: surface it and stop retrying
                // (see onclose). A status frame is NOT proof of a healthy PTY,
                // so it must not reset the reconnect backoff either.
                fatalError = true;
                setStatus("error");
                setErrorMessage(String(msg.message ?? "Terminal error."));
              }
            } catch {
              /* ignore non-JSON text frames */
            }
            return;
          }
          // Binary frame ⇒ real PTY bytes: auth passed and the stream is healthy,
          // so now it's safe to reset the reconnect backoff (only here, never on
          // a status frame, or a status:error first frame would look healthy).
          if (!gotData) {
            gotData = true;
            reconnectAttemptsRef.current = 0;
            socketReconnectAttemptsRef.current = 0;
            authRejectedRef.current = false;
            console.log("[Terminal] first frame", typeof ev.data);
          }
          term.write(new Uint8Array(ev.data as ArrayBuffer));
          if (!unmountedRef.current) setStatus("connected");
        };
        socket.onclose = (ev) => {
          console.log(
            "[Terminal] CLOSE code=%s clean=%s reason=%o unmounted=%s gotData=%s",
            ev.code,
            ev.wasClean,
            ev.reason,
            unmountedRef.current,
            gotData,
          );
          if (socketRef.current === socket) socketRef.current = null;
          // 1008 = the in-container server rejected the token (unauthorized). Re-provisioning gets a
          // fresh token, but if the rejection persists (e.g. config skew) the backoff escalates and
          // we stop at MAX rather than hammering. Cleared once a connection delivers a frame.
          if (ev.code === 1008) authRejectedRef.current = true;
          // Don't reconnect after a server-reported fatal error: it already told
          // us the condition is terminal, so retrying would just flip the overlay
          // back to "Reconnecting…" and loop a state the server called fatal.
          if (fatalError) return;
          if (!unmountedRef.current) {
            // Re-provision only when the shared cache is actually suspect: a 1008 (token rejected), or
            // a reconnect that already retried the cached URL and still failed
            // (socketReconnectAttempts > 0 — the sandbox was likely paused/reset and its e2b URL
            // changed). A first drop gets one reconnect on the cached URL, so a momentary blip on one
            // widget doesn't re-provision the whole canvas. This counts only socket-close reconnects,
            // not pre-socket connect failures, so a failed provision can't bust the cache on the
            // first real close.
            if (ev.code === 1008 || socketReconnectAttemptsRef.current > 0) {
              invalidateCanvasSandboxProvision(orgId, userId, canvasId);
            }
            socketReconnectAttemptsRef.current++;
            scheduleReconnect();
          }
        };
        socket.onerror = () => {
          console.log("[Terminal] ERROR (onerror)");
        };
      } catch (err) {
        if (!unmountedRef.current) {
          // A transient provision failure (5xx / network — e.g. zensei-api deploying, ALB 502/503/504)
          // retries patiently; anything else (a non-axios throw, or a 4xx like 403 lost-access) falls
          // to the bounded WS-reconnect path so it surfaces an "error" instead of spinning forever.
          if (isTransientProvisionError(err)) scheduleProvisionRetry();
          else scheduleReconnect();
        }
        console.error("[TerminalWidget] connect failed", err);
      }
    }

    term.onData((input) => {
      const socket = socketRef.current;
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(new TextEncoder().encode(input));
      }
    });
    term.onResize(() => sendResize());

    const resizeObserver = new ResizeObserver(() => safeFit());
    resizeObserver.observe(container);

    // First layout pass → fit, then connect with real cols/rows.
    const raf = requestAnimationFrame(() => {
      safeFit();
      connect();
    });

    // Persist the minted id (only when none exists yet) so reloads — and other
    // collaborators — re-attach to the same tmux session and the toolbar/agent
    // target the same one. If an id is already present we adopt it instead.
    if (canEdit && typeof data.terminalSessionId !== "string") {
      canvas.updateNodeData(id, { terminalSessionId: sessionId });
    }

    return () => {
      console.log("[Terminal] cleanup id=%s (unmount or effect re-run)", id);
      unmountedRef.current = true;
      cancelled = true;
      cancelAnimationFrame(raf);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      resizeObserver.disconnect();
      // Detach onclose BEFORE closing: a close fires async, and the next effect run resets
      // unmountedRef to false, so a delayed onclose from this (now-dead) socket would wrongly pass its
      // guard and invalidate the shared per-canvas provision cache for every widget. Dropping the
      // handler makes this socket's close a no-op.
      if (socketRef.current) socketRef.current.onclose = null;
      // Close the socket without a {type:"close"} frame: the bridge detaches the
      // PTY but leaves the tmux session running for the next open.
      socketRef.current?.close();
      socketRef.current = null;
      // Release this widget's hold on the shared provision; the last release drops the cached token.
      if (provisionIdentity) {
        releaseCanvasSandboxProvision(
          provisionIdentity.orgId,
          provisionIdentity.userId,
          provisionIdentity.canvasId,
        );
      }
      term.dispose();
      termRef.current = null;
      fitRef.current = null;
    };
    // Reconnect when the canonical session id changes (e.g. a collaborator's id
    // syncs in after we used a local fallback) so the PTY binds to the same tmux
    // session the toolbar/agent target. Also re-run when canEdit flips (view-only
    // ↔ editor) so we start or tear down the provisioned connection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasId, sessionId, canEdit, isCapture]);

  // ── Apply theme changes live ──────────────────────────────────────────────
  useEffect(() => {
    if (termRef.current) {
      termRef.current.options.theme = getTerminalTheme(themeId).theme;
    }
  }, [themeId]);

  const termBg = getTerminalTheme(themeId).theme.background ?? "#1e1e1e";

  const overlay =
    status !== "connected" && !isCapture
      ? renderOverlay(status, errorMessage)
      : null;

  return (
    <div
      className="relative flex h-full w-full flex-col overflow-hidden rounded-lg border border-black/10 shadow-sm dark:border-white/10"
      style={{ backgroundColor: termBg }}
    >
      {/* Title bar — NOT marked `nodrag`, so it's the grab handle for the node.
          A lightened tint of the terminal background so it reads as distinct
          window chrome across any theme. */}
      <div
        className={cn(
          "h-7 w-full shrink-0 border-b border-black/10 dark:border-white/10",
          canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-default",
        )}
        style={{
          backgroundColor: `color-mix(in srgb, ${termBg} 82%, #ffffff)`,
        }}
      />
      {/* nodrag/nowheel: keep typing, selecting, and scrolling inside the
          terminal from panning/zooming the canvas or dragging the node. */}
      <div
        ref={containerRef}
        className={cn(
          "nodrag nowheel min-h-0 w-full flex-1 p-2",
          canEdit ? "cursor-text" : "cursor-default",
        )}
      />
      {overlay}
    </div>
  );
}

// A provision failure worth retrying patiently: an axios error that is NOT a 4xx — i.e. a network /
// ALB failure (no numeric status, e.g. 503 with no body, connection reset) or a 5xx. A 4xx (403 lost
// access, 400 bad request) is permanent, and a non-axios throw is treated as permanent too, so both
// fall to the bounded WS-reconnect path. Mirrors the queryClient retry rule (don't retry 4xx).
function isTransientProvisionError(err: unknown): boolean {
  if (!isAxiosError(err)) return false;
  const status = err.response?.status;
  if (typeof status !== "number") return true; // no response → network / ALB / timeout
  return status >= 500;
}

function renderOverlay(status: ConnState, errorMessage: string) {
  const text =
    status === "unavailable"
      ? "Terminal is available to editors only."
      : status === "connecting"
        ? "Connecting to container…"
        : status === "reconnecting"
          ? "Reconnecting…"
          : errorMessage || "Terminal error.";
  const showSpinner = status === "connecting" || status === "reconnecting";
  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-0 flex items-center justify-center",
        "bg-black/55 text-sm text-white/90 backdrop-blur-[1px]",
      )}
    >
      <div className="flex items-center gap-2">
        {showSpinner && (
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />
        )}
        <span>{text}</span>
      </div>
    </div>
  );
}
